/** Sentinel value for the map campaign filter "all campaigns" overview mode. */
export const ALL_CAMPAIGNS_ID = "__all__";

export function isAllCampaignsMode(campaignId: string | undefined | null): boolean {
    return campaignId === ALL_CAMPAIGNS_ID;
}

/** True when a specific campaign is selected (not empty, not all-campaigns overview). */
export function isCampaignSelected(campaignId: string | undefined | null): boolean {
    return !!campaignId && !isAllCampaignsMode(campaignId);
}

/** True when the map should load address data (single campaign or all-campaigns overview). */
export function hasMapCampaignScope(campaignId: string | undefined | null): boolean {
    return isCampaignSelected(campaignId) || isAllCampaignsMode(campaignId);
}

import type { AddressFeatureProperties } from "./types";

/**
 * All-campaigns overview: show only delivered addresses that are not blocked or hidden.
 *
 * `notDeliveredSince` and `deliveredSince` are opposite ends of the same question — mutually
 * exclusive in the UI, but both accepted here so callers don't have to pick one to pass through.
 * - `notDeliveredSince`: addresses that need attention — never delivered, or last delivered
 *   before that date. Only meaningful together with `showUndelivered` — the undelivered
 *   addresses have to be in the fetched set in the first place for this to have anything to
 *   filter over.
 * - `deliveredSince`: the opposite — only addresses last delivered on or after that date
 *   (never-delivered addresses never match). Works regardless of `showUndelivered`.
 * - `deliveredUntil`: only meaningful together with `deliveredSince` — narrows it to a closed
 *   window (both dates inclusive) instead of an open-ended "since". Ignored on its own.
 */
export function isAllCampaignsVisibleAddress(
    properties: AddressFeatureProperties,
    showUndelivered = false,
    notDeliveredSince?: string | null,
    deliveredSince?: string | null,
    deliveredUntil?: string | null
): boolean {
    if (properties.isBlocked || properties.isHidden) {
        return false;
    }

    const statuses = properties.flyerStatuses ?? [];
    const hasDelivery = statuses.some((fs) => fs.deliveredQuantity > 0 || fs.isNoThanks);
    const lastDeliveredAt = hasDelivery
        ? statuses.reduce((latest, fs) => (fs.lastDeliveredAt > latest ? fs.lastDeliveredAt : latest), "")
        : null;

    if (deliveredSince) {
        if (lastDeliveredAt === null || lastDeliveredAt < deliveredSince) {
            return false;
        }
        // Compare against end-of-day so a delivery any time on the "until" date still counts.
        return !deliveredUntil || lastDeliveredAt <= `${deliveredUntil}T23:59:59.999Z`;
    }

    if (notDeliveredSince) {
        if (!hasDelivery) {
            return true;
        }
        return lastDeliveredAt !== null && lastDeliveredAt < notDeliveredSince;
    }

    if (showUndelivered) {
        return true;
    }
    return hasDelivery;
}

/** Omit campaignId from API params in all-campaigns overview mode. */
export function resolveMapCampaignIdForApi(campaignId: string | undefined | null): string | undefined {
    if (!campaignId || isAllCampaignsMode(campaignId)) {
        return undefined;
    }
    return campaignId;
}
